import { useEffect, useState } from 'react'
import { Spinner } from '@/components/Spinner'
import { useCustomerSearch, type CustomerMatch } from '@/hooks/useCustomerSearch'
import { cn } from '@/lib/utils'

// The list that opens when an operator types "@" in the composer. Five matches:
// enough that the right person is nearly always there, few enough to scan
// without scrolling and without covering the conversation.

/** Matches shown at once. */
export const MENTION_RESULT_LIMIT = 5

interface MentionPopupProps {
  query: string
  onChoose: (customer: CustomerMatch) => void
  onDismiss: () => void
  /** Registers the keyboard handler so the textarea can hand off ↑/↓/Enter/Esc. */
  onKeyHandlerReady: (handler: ((key: string) => boolean) | null) => void
}

export function MentionPopup({ query, onChoose, onDismiss, onKeyHandlerReady }: MentionPopupProps) {
  const { matches, searching, active } = useCustomerSearch(query, { limit: MENTION_RESULT_LIMIT })
  const [highlight, setHighlight] = useState(0)

  // A new result set always starts at the top match — keeping an old index would
  // insert whoever happens to sit at that position now.
  useEffect(() => setHighlight(0), [matches])

  useEffect(() => {
    // Returns true when the key was consumed, so the textarea knows not to also
    // send the message or move the caret.
    const handler = (key: string): boolean => {
      if (key === 'Escape') {
        onDismiss()
        return true
      }
      if (matches.length === 0) return false
      if (key === 'ArrowDown') {
        setHighlight((i) => (i + 1) % matches.length)
        return true
      }
      if (key === 'ArrowUp') {
        setHighlight((i) => (i - 1 + matches.length) % matches.length)
        return true
      }
      if (key === 'Enter' || key === 'Tab') {
        onChoose(matches[highlight] ?? matches[0])
        return true
      }
      return false
    }
    onKeyHandlerReady(handler)
    return () => onKeyHandlerReady(null)
  }, [matches, highlight, onChoose, onDismiss, onKeyHandlerReady])

  return (
    <div className="border-t border-border bg-card px-3 py-2">
      <div className="overflow-hidden rounded-lg border border-border bg-background">
        {searching ? (
          <div className="flex items-center gap-2 px-3 py-2.5 text-muted-foreground">
            <Spinner size={14} className="text-current" />
            <span className="text-[13px]">Searching customers…</span>
          </div>
        ) : !active ? (
          <p className="px-3 py-2.5 text-[13px] text-muted-foreground">
            Keep typing a name, email, or phone to mention a customer.
          </p>
        ) : matches.length === 0 ? (
          <p className="px-3 py-2.5 text-[13px] text-muted-foreground">No customers match “{query.trim()}”.</p>
        ) : (
          matches.map((m, i) => (
            <button
              key={m.id}
              type="button"
              // The textarea keeps focus so typing continues to filter, which
              // means the highlight has to be driven by hover, not focus.
              onMouseEnter={() => setHighlight(i)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onChoose(m)}
              className={cn(
                'flex w-full items-baseline justify-between gap-3 border-t border-muted px-3 py-2 text-left first:border-t-0',
                i === highlight && 'bg-accent',
              )}
            >
              <span className="min-w-0 flex-1 truncate text-[14px] text-foreground">
                {m.name ?? 'Unnamed customer'}
              </span>
              {m.phone ? <span className="shrink-0 text-[12px] text-muted-foreground">{m.phone}</span> : null}
            </button>
          ))
        )}
      </div>
    </div>
  )
}
